
import React from 'react';
import { Card, CardContent } from "@/components/ui/card";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { Textarea } from "@/components/ui/textarea";

interface StrategyFormProps {
  onSubmit: (data: any) => void;
  initialData?: any;
}

export const StrategyForm = ({ onSubmit, initialData }: StrategyFormProps) => {
  const [name, setName] = React.useState(initialData?.name || '');
  const [description, setDescription] = React.useState(initialData?.description || '');
  const [exchange, setExchange] = React.useState(initialData?.exchange || '');
  const [currency, setCurrency] = React.useState(initialData?.currency || 'USDT');
  const [risk_level, setRiskLevel] = React.useState(initialData?.risk_level || 1);
  const [min_investment, setMinInvestment] = React.useState(initialData?.min_investment || '100');
  const [plan, setPlan] = React.useState(initialData?.plan || 'free');
  const [active, setActive] = React.useState(initialData?.active !== false);
  const [compounding, setCompounding] = React.useState(initialData?.compounding || false);
  const [risk_corridor, setRiskCorridor] = React.useState(initialData?.risk_corridor || false);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    
    onSubmit({
      strategy: {
        name,
        description,
        exchange,
        currency,
        risk_level,
        min_investment: parseFloat(min_investment),
        plan,
        active,
        compounding,
        risk_corridor
      }
    });
  };

  return (
    <form onSubmit={handleSubmit}>
      <Card>
        <CardContent className="pt-6">
          <div className="space-y-4">
            <div>
              <Label htmlFor="name">Strategy Name</Label>
              <Input 
                id="name" 
                value={name} 
                onChange={(e) => setName(e.target.value)} 
                required 
              />
            </div>
            
            <div>
              <Label htmlFor="description">Description</Label>
              <Textarea 
                id="description" 
                value={description} 
                onChange={(e) => setDescription(e.target.value)} 
                rows={3}
              />
            </div>
            
            <div>
              <Label htmlFor="exchange">Exchange</Label>
              <Select value={exchange} onValueChange={setExchange} required>
                <SelectTrigger>
                  <SelectValue placeholder="Select exchange" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="OKX">OKX</SelectItem>
                  <SelectItem value="Bybit">Bybit</SelectItem>
                  <SelectItem value="Bitget">Bitget</SelectItem>
                  <SelectItem value="Phemex">Phemex</SelectItem>
                </SelectContent>
              </Select>
            </div>
            
            <div>
              <Label htmlFor="currency">Base Currency</Label>
              <Select value={currency} onValueChange={setCurrency} required>
                <SelectTrigger>
                  <SelectValue placeholder="Select currency" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="USDT">USDT</SelectItem>
                  <SelectItem value="USDC">USDC</SelectItem>
                  <SelectItem value="BTC">BTC</SelectItem>
                  <SelectItem value="ETH">ETH</SelectItem>
                  <SelectItem value="USD">USD</SelectItem>
                  <SelectItem value="EUR">EUR</SelectItem>
                </SelectContent>
              </Select>
            </div>
            
            <div>
              <Label htmlFor="risk-level">Risk Level</Label>
              <Select value={risk_level.toString()} onValueChange={(val) => setRiskLevel(parseInt(val))} required>
                <SelectTrigger>
                  <SelectValue placeholder="Select risk level" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="1">1 - Low Risk</SelectItem>
                  <SelectItem value="2">2 - Medium Risk</SelectItem>
                  <SelectItem value="3">3 - High Risk</SelectItem>
                </SelectContent>
              </Select>
            </div>
            
            <div>
              <Label htmlFor="min-investment">Minimum Investment</Label>
              <Input 
                id="min-investment" 
                type="number" 
                value={min_investment} 
                onChange={(e) => setMinInvestment(e.target.value)} 
                required 
              />
            </div>
            
            <div>
              <Label htmlFor="plan">Plan Type</Label>
              <Select value={plan} onValueChange={setPlan} required>
                <SelectTrigger>
                  <SelectValue placeholder="Select plan type" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="free">Free</SelectItem>
                  <SelectItem value="premium">Premium</SelectItem>
                </SelectContent>
              </Select>
            </div>
            
            <div className="flex flex-col space-y-2">
              <div className="flex items-center space-x-2">
                <Switch 
                  id="active" 
                  checked={active} 
                  onCheckedChange={setActive} 
                />
                <Label htmlFor="active">Strategy Active</Label>
              </div>
              
              <div className="flex items-center space-x-2">
                <Switch 
                  id="compounding" 
                  checked={compounding} 
                  onCheckedChange={setCompounding} 
                />
                <Label htmlFor="compounding">Compounding</Label>
              </div>
              
              <div className="flex items-center space-x-2">
                <Switch 
                  id="risk-corridor" 
                  checked={risk_corridor} 
                  onCheckedChange={setRiskCorridor} 
                />
                <Label htmlFor="risk-corridor">Risk Corridor</Label>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>
    </form>
  );
};
